import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { getPracticeId } from './clincheck';
import { updateError } from './user';
import { setLoader } from './loader';

const initialState = {
    isLoading: false,
    practiceChatBotsGlobal: null,
    individualChatBotGlobal: null,
    loadingForChatbot: false
};


const slice = createSlice({
    name: 'practiceChatbot',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        setPracticeChatBotsGlobal(state, action) {
            state.practiceChatBotsGlobal = action.payload;
        },
        setIndividualChatBotGlobal(state, action) {
            state.individualChatBotGlobal = action.payload;
        },
        setLoadingForChatbot(state, action) {
            state.loadingForChatbot = action.payload;
        },
    }
});

export default slice.reducer;

export const { setPracticeChatBotsGlobal, setIndividualChatBotGlobal, reset } = slice.actions;

const handleLoading = (state) => {
    if (state === false) {
        setTimeout(() => {
            dispatch(slice.actions.setLoadingForChatbot(state));
        }, 500);
    } else {
        dispatch(slice.actions.setLoadingForChatbot(state));
    }
};

export function handleGetPracticeChatBots() {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            handleLoading(true);
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/?ordering=-updated_at`);
            if (response.status === 200) {
                handleLoading(false);
                dispatch(slice.actions.setPracticeChatBotsGlobal(response.data?.results));
            } else {
                handleLoading(false);
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            handleLoading(false);
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
export function handleGetIndividualChatBot(chatbotId) {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            handleLoading(true);
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/${chatbotId}/`);
            if (response.status === 200) {
                handleLoading(false);
                dispatch(slice.actions.setIndividualChatBotGlobal(response.data));
            } else {
                handleLoading(false);
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            handleLoading(false);
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
export function handleRemoveChatbot(chatbotId) {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/${chatbotId}/`, 'delete');
            if (response.status === 204) {
                dispatch(handleGetPracticeChatBots());
                dispatch(updateError({ message: 'Chatbot deleted successfully', success: true }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
export function handleCreatePracticeChatBots(data) {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/`, 'post', data.body);
            if (response.status === 201) {
                if (data.handleSuccess) {
                    data.handleSuccess();
                }
                dispatch(updateError({
                    message: 'Chatbot created successfully',
                    success: true
                }));
            } else {
                dispatch(setLoader(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(setLoader(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
export function handleDuplicateChatbot(chatbotId) {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/${chatbotId}/duplicate/`, 'post');
            if (response.status === 201) {
                dispatch(handleGetPracticeChatBots());
                dispatch(updateError({ message: 'Chatbot duplicated successfully', success: true }));
            } else {
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
/**
 * Targeted PATCH for the super-admin chatbot console. Updates ONLY the
 * customisations flags and/or the platform config JSON, without disturbing
 * nested treatments/messages. Every existing platform is re-sent with its id
 * (so none is treated as removed) and only settings.config is modified.
 *
 * @param {object} args
 * @param {object} args.chatbot       full chatbot object (from retrieve/list)
 * @param {object} [args.customisations] flag values to set (sent in full)
 * @param {object} [args.configPatch]    config keys to merge into platform config
 * @param {function} [args.handleSuccess]
 * @param {function} [args.handleError]
 */
export function handleQuickUpdateChatbot({ chatbot, customisations, configPatch, handleSuccess, handleError }) {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const practiceId = getPracticeId();
            const body = {};
            if (customisations) {
                body.customisations = { id: chatbot?.customisations?.id, ...customisations };
            }
            if (configPatch) {
                body.chatbot_platforms = (chatbot?.chatbot_platforms || []).map((p) => ({
                    id: p.id,
                    settings: {
                        id: p?.settings?.id,
                        // config is a JSON blob (replaced wholesale) — merge to preserve other keys.
                        config: { ...(p?.settings?.config || {}), ...configPatch },
                    },
                }));
            }
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/${chatbot.id}/`, 'patch', body);
            if (response.status === 200) {
                if (handleSuccess) handleSuccess(response.data);
                dispatch(updateError({ message: 'Chatbot updated', success: true }));
            } else {
                if (handleError) handleError(response.data);
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            if (handleError) handleError(error);
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}
export function handleEditPracticeChatBots(data) {
    return async () => {
        if (!hasFeatureAccess('chatbot')) return null;
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/chatbots/${data.chatbotId}/`, 'put', data.body);
            if (response.status === 200) {
                if (data.handleSuccess) {
                    data.handleSuccess();
                }
                dispatch(updateError({
                    message: 'Chatbot edited successfully',
                    success: true
                }));
            } else {
                dispatch(setLoader(false));
                dispatch(updateError({ message: JSON.stringify(response.data), success: false }));
            }
        } catch (error) {
            dispatch(setLoader(false));
            dispatch(updateError({ message: JSON.stringify(error.message), success: false }));
        }
    };
}